import { useStore } from 'zustand';

import { constructionSteps, LessonMode } from '@/domain/model';
import { type EditorStore } from '@/store';

import { colour, space, text } from './theme';

/**
 * Classroom controls: mode, scaffolding, and step-by-step replay.
 *
 * ## Why the replay is a slider rather than a play button
 *
 * A teacher explaining a construction moves back and forth — "and if we had not drawn this
 * one…" — at the pace of the room, not at a pace chosen in advance. An animation would have
 * to be paused, rewound and re-timed constantly. A slider is scrubbing, which is the gesture
 * the task actually is. The step buttons are there for the common case of advancing one at a
 * time without looking down.
 *
 * ## The bar stays visible while presenting
 *
 * Everything else disappears in presentation mode; this does not, because the replay is the
 * thing being presented *with*. It fades instead, so it is available without competing with
 * the drawing.
 *
 * ## Why the axes toggle is here rather than in the inspector
 *
 * The rail answers "what can I do with *this selection*", and the axes belong to no selection —
 * they are behind everything, always. They also want to be reachable while presenting, which is
 * exactly when a clean canvas matters most and exactly when the rail is not on screen. That
 * makes this bar, the one place kept for view state, their only correct address.
 */

export interface LessonBarProps {
  readonly store: EditorStore;
}

const MODES: readonly { value: LessonMode; label: string; hint: string }[] = [
  { value: LessonMode.Teacher, label: 'Soạn', hint: 'Đầy đủ công cụ. Dùng khi bạn đang soạn bài.' },
  {
    value: LessonMode.Student,
    label: 'Khám phá',
    hint: 'Học sinh kéo và đo được, nhưng không phá được hình.',
  },
  {
    value: LessonMode.Presentation,
    label: 'Trình chiếu',
    hint: 'Chỉ còn hình, để chiếu lên màn. Không sửa được.',
  },
];

export function LessonBar({ store }: LessonBarProps) {
  const document = useStore(store, (state) => state.document);
  const lesson = useStore(store, (state) => state.lesson);
  const showGridAxes = useStore(store, (state) => state.showGridAxes);
  const commands = useStore(store, (state) => state.commands);

  const total = constructionSteps(document).length;
  const shown = lesson.revealed ?? total;
  const presenting = lesson.mode === LessonMode.Presentation;

  return (
    <div
      className="es-panel"
      style={{
        // Placed by the shell. See the note in `FileBar` — both used to claim the same slot.
        display: 'flex',
        alignItems: 'center',
        gap: space.base,
        pointerEvents: 'auto',
        padding: space.snug,
        font: text.body,
        /*
         * It fades while presenting rather than disappearing, and comes back on hover: the
         * replay is the thing being presented *with*, so it has to be reachable — but a full
         * strength control bar across the top of a projected figure is the first thing a class
         * looks at instead of the drawing.
         */
        opacity: presenting ? 0.45 : 1,
        transition: 'opacity var(--es-quick)',
      }}
      onPointerEnter={(event) => {
        event.currentTarget.style.opacity = '1';
      }}
      onPointerLeave={(event) => {
        event.currentTarget.style.opacity = presenting ? '0.45' : '1';
      }}
    >
      {/*
        A segmented control rather than three separate buttons.

        The three are mutually exclusive and always all present, which is exactly what a segment
        group means and what three bordered buttons in a row do not: side by side with their own
        outlines they read as three independent actions, and the one that happens to be blue
        reads as the *recommended* one rather than as the current one.
      */}
      <div
        style={{
          display: 'flex',
          padding: 2,
          gap: 2,
          borderRadius: 'var(--es-radius-sm)',
          background: colour.wash,
        }}
      >
        {MODES.map((mode) => (
          <button
            key={mode.value}
            type="button"
            className={lesson.mode === mode.value ? 'es-btn es-seg es-seg--on' : 'es-btn es-seg'}
            aria-pressed={lesson.mode === mode.value}
            title={mode.hint}
            onClick={() => {
              commands.setLesson({ mode: mode.value });
            }}
            style={{ padding: '3px 10px', font: text.small }}
          >
            {mode.label}
          </button>
        ))}
      </div>

      <span className="es-divider" style={{ width: 1, height: 18 }} />

      <ToggleButton
        label="Ẩn nét phụ"
        title="Ẩn những đối tượng bạn đã đánh dấu là nét dựng phụ trong bảng Đối tượng"
        pressed={!lesson.showConstruction}
        onClick={() => {
          commands.setLesson({ showConstruction: !lesson.showConstruction });
        }}
      />

      <ToggleButton
        label="Ẩn trục"
        title="Ẩn hai trục ở nền canvas đi qua gốc toạ độ. Lưới vẫn còn, và Hệ trục toạ độ bạn tự thêm vào bản vẽ không bị ảnh hưởng."
        pressed={!showGridAxes}
        onClick={() => {
          commands.setShowGridAxes(!showGridAxes);
        }}
      />

      <span className="es-divider" style={{ width: 1, height: 18 }} />

      <div style={{ display: 'flex', alignItems: 'center', gap: space.snug }}>
        <StepButton
          label="◀"
          title="Lùi một bước"
          disabled={shown <= 0}
          onClick={() => {
            commands.setLesson({ revealed: Math.max(0, shown - 1) });
          }}
        />

        <input
          type="range"
          className="es-slider"
          min={0}
          max={total}
          step={1}
          value={shown}
          aria-label="Construction step"
          onChange={(event) => {
            const next = Number(event.target.value);
            // Dragging to the end means "all of it", including anything drawn afterwards —
            // otherwise the replay would silently pin itself to today's object count.
            commands.setLesson({ revealed: next >= total ? null : next });
          }}
          style={{ width: 132 }}
        />

        <StepButton
          label="▶"
          title="Tiến một bước"
          disabled={shown >= total}
          onClick={() => {
            const next = shown + 1;
            commands.setLesson({ revealed: next >= total ? null : next });
          }}
        />

        <span
          className="es-numeric"
          style={{
            minWidth: 42,
            fontSize: 11,
            textAlign: 'right',
            color: lesson.revealed === null ? colour.faint : colour.ink,
          }}
        >
          {lesson.revealed === null ? 'cả hình' : `${String(shown)}/${String(total)}`}
        </span>
      </div>
    </div>
  );
}

/**
 * A button that reports a state rather than firing an action.
 *
 * Filled when pressed, because both of these are *subtractive*: the lit button is the one
 * that is currently taking something off the screen. A user glancing at the bar to work out
 * why the drawing looks unfamiliar needs the answer to be the thing that stands out.
 */
function ToggleButton({
  label,
  title,
  pressed,
  onClick,
}: {
  readonly label: string;
  readonly title: string;
  readonly pressed: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={pressed ? 'es-btn es-btn--on' : 'es-btn'}
      title={title}
      aria-pressed={pressed}
      onClick={onClick}
      style={{ padding: '4px 9px', font: text.small }}
    >
      {label}
    </button>
  );
}

function StepButton({
  label,
  title,
  disabled,
  onClick,
}: {
  readonly label: string;
  readonly title: string;
  readonly disabled: boolean;
  readonly onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="es-btn"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={onClick}
      style={{ width: 24, padding: '3px 0', fontSize: 9 }}
    >
      {label}
    </button>
  );
}
